import React from "react";
import { Anchor, ChevronDown, HelpCircle } from "lucide-react";
import { getPublicComponentCopy, isRtlPublicLang } from "@/lib/publicComponentCopy";

const marinaFaqs = [
  {
    question: "How do I find marinas near my current location?",
    answer: "Allow location access on the marinas page, then adjust the search radius to see nearby marinas sorted by distance.",
  },
  {
    question: "Can I use Marstick to book a marina berth?",
    answer: "Marstick shows marina details and booking status when available, but always confirm berth availability directly with the marina before arrival.",
  },
  {
    question: "What information should I check before choosing a marina?",
    answer: "Check distance, available berths, price, facilities, VHF channel, contact details, and whether the marina accepts booking requests.",
  },
  {
    question: "Are marina distances exact?",
    answer: "Distances are useful planning estimates based on coordinates. Always verify your route with official charts and local navigation guidance.",
  },
  {
    question: "What does the search radius control do?",
    answer: "The radius control filters marinas by distance from your current location, helping you quickly narrow results to nearby options.",
  },
  {
    question: "Can I navigate directly to a marina from Marstick?",
    answer: "Yes. Marina cards include map and route-planning actions so you can open the destination in mapping tools.",
  },
  {
    question: "What facilities can I compare between marinas?",
    answer: "Common facilities include fuel, water, electricity, Wi‑Fi, restaurants, showers, laundry, and repair services when the data is available.",
  },
  {
    question: "Why do some marinas have fewer details?",
    answer: "Some marina records may only include basic public information. We recommend contacting the marina directly for final confirmation.",
  },
  {
    question: "Can sailors rate marinas?",
    answer: "Yes. When rating is available, sailors can share feedback to help others understand the marina experience.",
  },
  {
    question: "Should I rely only on Marstick for marina arrival planning?",
    answer: "No. Use Marstick for planning support, then confirm local conditions, charts, opening hours, weather, and berth availability with official sources.",
  },
  {
    question: "What if no marinas appear in my radius?",
    answer: "Increase the radius or search a different nearby coastline, port, island, or city.",
  },
  {
    question: "Does Marstick work on mobile while sailing?",
    answer: "Yes. The marina pages are designed for fast mobile use, but connection quality at sea may vary.",
  },
];

export default function PublicMarinasFAQ({ lang = "en" }) {
  const [openIndex, setOpenIndex] = React.useState(0);
  const copy = getPublicComponentCopy(lang).faq;
  const marinaFaqs = copy.items.map(([question, answer]) => ({ question, answer }));
  const isRtl = isRtlPublicLang(lang);

  return (
    <section className="mt-10 overflow-hidden rounded-[2rem] border border-primary/15 bg-card shadow-xl" dir={isRtl ? "rtl" : "ltr"}>
      <div className="grid gap-6 bg-gradient-to-br from-ocean via-atlantic to-primary p-6 text-white md:grid-cols-[0.8fr_1.2fr] md:p-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-2 text-sm font-black uppercase tracking-[0.18em] !text-white">
            <HelpCircle className="h-4 w-4 !text-white" /> {copy.badge}
          </div>
          <h2 className="mt-4 font-heading text-3xl font-black !text-white md:text-4xl">{copy.title}</h2>
          <p className="mt-3 leading-7 !text-white/90">{copy.intro}</p>
        </div>
        <div className="rounded-3xl border border-white/15 bg-white/10 p-5 backdrop-blur">
          <Anchor className="h-8 w-8 text-cyan-100" />
          <p className="mt-3 font-bold text-cyan-50">{copy.tip}</p>
        </div>
      </div>

      <div className="grid gap-3 p-4 md:p-6">
        {marinaFaqs.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={item.question} className="rounded-2xl border border-border bg-background/70">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                className="flex w-full items-center justify-between gap-4 p-4 text-left font-black text-foreground md:p-5"
              >
                <span>{item.question}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-border px-4 pb-4 pt-3 text-muted-foreground md:px-5 md:pb-5">
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}